import { FC, useEffect, useRef } from "react";

import { copy } from "shared/assets";
import { StyledPermissionGroup } from "./components/styles";

import { sanitizeId, useDisabledDependency, useFirstCheckedGroupPermission } from "./helpers";
import { PermissionField, PermissionFieldBaseProps } from "./components";

const {
  permissions: { editPermissions: permissionsCopy },
} = copy;

interface PermissionGroupProps extends PermissionFieldBaseProps {
  setValue: (name: string, checked: boolean) => void;
  watchAllFields: { [name: string]: boolean };
  firstPermission: string;
  disabledDependencies?: string[];
  uncheckDependencies?: string[];
  isLast?: boolean;
  rootGroupId: string;
}

export const PermissionGroup: FC<PermissionGroupProps> = ({
  name,
  value,
  setValue,
  watchAllFields,
  firstPermission,
  disabledDependencies,
  uncheckDependencies,
  isLast,
  rootGroupId,
  children,
  ...rest
}) => {
  const { isDisabled } = useDisabledDependency({
    watchAllFields,
    disabledDependencies,
  });

  const { firstCheckedGroupPermission } = useFirstCheckedGroupPermission({
    name,
    rootGroupId,
    watchAllFields,
  });

  // update the group value based on the first dependency value
  const prevDepValue = useRef(watchAllFields[firstPermission]);
  useEffect(() => {
    const depValue = watchAllFields[firstPermission];

    if (prevDepValue.current === depValue) return;

    if (depValue && !watchAllFields[name]) {
      setValue(name, true);
    }

    if (!depValue && watchAllFields[name]) {
      setValue(name, false);
    }

    prevDepValue.current = depValue;
  }, [firstPermission, name, setValue, watchAllFields]);

  const handleChange = (checked: boolean) => {
    if (checked) {
      setValue(firstPermission, true);
      return;
    }

    setValue(firstPermission, false);

    // wait till first permission is unchecked
    // to remove the disabled from its dependencies
    setTimeout(() => {
      uncheckDependencies?.forEach(dep => {
        const depField = document.querySelector(`#${sanitizeId(dep)}`) as HTMLInputElement;

        if (!depField?.disabled) {
          setValue(dep, false);
        }
      });
    });
  };

  return (
    <StyledPermissionGroup isActive={value} isLast={isLast}>
      <PermissionField
        name={name}
        value={value}
        disabled={isDisabled}
        handleChange={handleChange}
        isGroup
        hoverText={permissionsCopy.messages.disabled
          .replace("%s", firstCheckedGroupPermission?.label!)
          .toString()}
        {...rest}
      />
      {children}
    </StyledPermissionGroup>
  );
};
